import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';
import { TRIGGER_OBJECT } from '../../constants/ClickstreamConstants';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';

const EVENT_IDS = {
  DOG_SIZE_ALLOWED: '6619',
  NUMBER_OF_DOGS_ALLOWED: '6618',
  PET_POLICY_NEGOTIABLE_CHECKBOX: '6617',
  NUMBER_OF_LARGE_DOGS_ALLOWED: '6616',
  NUMBER_OF_SMALL_DOGS_ALLOWED: '6615',
  NUMBER_OF_CATS_ALLOWED: '6614',
  PETS_ALLOWED_NO: '6613',
  PETS_ALLOWED_YES: '6612',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6619
const DogSizeAllowed = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DOG_SIZE_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'post_activation',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'dog_size_allowed',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6618
const NumberOfDogsAllowed = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_DOGS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'post_activation',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'number_of_dogs_allowed',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6617
const PetPolicyNegotiableCheckbox = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  triggerObject?: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PET_POLICY_NEGOTIABLE_CHECKBOX,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'pets_negotiable_checkbox',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6616
const NumberOfLargeDogsAllowed = ({
  currentProperty,
  user,
  triggerObject,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_LARGE_DOGS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'number_of_large_dogs',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6615
const NumberOfSmallDogsAllowed = ({
  currentProperty,
  user,
  triggerObject,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) => ({
  envelope: {
    event_template_id: '168',
    event_template_version_id: '1',
    event_type_id: EVENT_IDS.NUMBER_OF_SMALL_DOGS_ALLOWED,
    event_type_version_id: '1',
  },
  clickstream_trigger: {
    trigger_location_nm: 'posting_path',
    trigger_type_nm: 'interaction',
    trigger_object_nm: triggerObject,
    trigger_source_nm: 'number_of_small_dogs',
  },
  semantic: {
    semantic_event_nm: 'set_listing_criteria',
    topic_tag_txt: ['zrm_properties'],
  },
  ...getZrmListingInfo(currentProperty),
  ...getZrmUserInfo(user),
});

// https://dataportal.zillowgroup.net/event-registry/6614
const NumberOfCatsAllowed = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  triggerObject?: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_CATS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'number_of_cats_allowed',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6613
const PetsAllowedNo = ({
  currentProperty,
  user,
  triggerObject,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PETS_ALLOWED_NO,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'pets_allowed_no',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6612
const PetsAllowedYes = ({
  currentProperty,
  user,
  triggerObject,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PETS_ALLOWED_YES,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'pets_allowed_yes',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const PetPolicyAndPetFeeCSEvents = {
  DogSizeAllowed,
  NumberOfCatsAllowed,
  NumberOfDogsAllowed,
  NumberOfLargeDogsAllowed,
  NumberOfSmallDogsAllowed,
  PetPolicyNegotiableCheckbox,
  PetsAllowedNo,
  PetsAllowedYes,
};

// Clickstream visualizer data-eventid attributes
DogSizeAllowed.data_event_id = EVENT_IDS.DOG_SIZE_ALLOWED;
NumberOfDogsAllowed.data_event_id = EVENT_IDS.NUMBER_OF_DOGS_ALLOWED;
PetPolicyNegotiableCheckbox.data_event_id = EVENT_IDS.PET_POLICY_NEGOTIABLE_CHECKBOX;
NumberOfLargeDogsAllowed.data_event_id = EVENT_IDS.NUMBER_OF_LARGE_DOGS_ALLOWED;
NumberOfSmallDogsAllowed.data_event_id = EVENT_IDS.NUMBER_OF_SMALL_DOGS_ALLOWED;
NumberOfCatsAllowed.data_event_id = EVENT_IDS.NUMBER_OF_CATS_ALLOWED;
PetsAllowedNo.data_event_id = EVENT_IDS.PETS_ALLOWED_NO;
PetsAllowedYes.data_event_id = EVENT_IDS.PETS_ALLOWED_YES;

export default PetPolicyAndPetFeeCSEvents;
